import React, { useContext, useEffect } from 'react';
import { getAllPages, getAllPosts, getMenu, getSettings } from './api/content-api';
import ContentContext from './store/content-context';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Page from './model/Page';
import ScrollToTop from './pages/ScrollToTop';
import ErrorPage from './pages/ErrorPage';
import RegisterForm from './components/Forms/Register/RegisterForm';
import RegisterForeignStudent from './components/Forms/Register/RegisterForeignStudent';
import LoginForm from './components/Forms/LoginForm';
import ResetPasswordForm from './components/Forms/ResetPasswordForm';
import {
	accountPage,
	confirmAccountPage,
	foreignRegistrationPage,
	loginPage,
	panelPage,
	passwordResetPage,
	registrationPage,
} from './store/fixed-routing';
import ConfirmAccountPage from './pages/ConfirmAccountPage';
import PanelPage from './pages/PanelPage';
import AccountPage from './pages/AccountPage';
import EmailVerification from './pages/EmailVerification';
import { Helmet } from 'react-helmet';
import AuthContext from './store/auth-context';
import { debugError } from './utils/debug';
import { getVariantCapability } from './config/variant-profile';

function App() {
	const { pages, setPages, setMenu, setSettings, registrationEnabled } = useContext(ContentContext);
	const location = useLocation();
	const publicCms = getVariantCapability('public_cms');
	const isPortalRoute =
		location.pathname === loginPage ||
		location.pathname === registrationPage ||
		location.pathname === foreignRegistrationPage ||
		location.pathname === passwordResetPage ||
		location.pathname === confirmAccountPage ||
		location.pathname === accountPage ||
		location.pathname.startsWith(panelPage) ||
		location.pathname.startsWith('/weryfikacja-email');

	useEffect(() => {
		const fetchData = async () => {
			const settings = await getSettings();
			setSettings(settings);
		};
		fetchData().catch((error) => debugError('App fetchData error:', error));
	}, []);

	useEffect(() => {
		if (!publicCms || isPortalRoute || pages.length > 0) {
			return;
		}
		const fetchPagesAndPosts = async () => {
			const pages = await getAllPages();
			const posts = await getAllPosts();
			const menu = await getMenu();
			setPages([...pages, ...posts]);
			setMenu(menu);
		};
		fetchPagesAndPosts().catch((error) => debugError('App fetchPagesAndPosts error:', error));
	}, [publicCms, isPortalRoute, pages.length, setPages]);

	const headData = {
		title: process.env.REACT_APP_TITLE,
		meta: [
			{ name: 'description', content: process.env.REACT_APP_DESCRIPTION },
			{ name: 'keywords', content: process.env.REACT_APP_KEYWORDS },
			{ name: 'robots', content: 'index, follow' },
			{ name: 'twitter:card', content: process.env.REACT_APP_OG_TWITTER_CARD },
			{ name: 'twitter:title', content: process.env.REACT_APP_OG_TITLE },
			{ name: 'twitter:description', content: process.env.REACT_APP_OG_DESCRIPTION },
			{ name: 'twitter:image', content: process.env.REACT_APP_OG_IMAGE },
			{ property: 'og:title', content: process.env.REACT_APP_OG_TITLE },
			{ property: 'og:description', content: process.env.REACT_APP_OG_DESCRIPTION },
			{ property: 'og:type', content: 'website' },
			{ property: 'og:url', content: process.env.REACT_APP_CANONICAL },
			{ property: 'og:image', content: process.env.REACT_APP_OG_IMAGE },
			{ property: 'og:image:width', content: process.env.REACT_APP_OG_IMAGE_W },
			{ property: 'og:image:height', content: process.env.REACT_APP_OG_IMAGE_H },
			{ property: 'og:site_name', content: process.env.REACT_APP_OG_TITLE },
			{ property: 'og:locale', content: 'pl_PL' },
		],
		link: [
			{ rel: 'canonical', href: process.env.REACT_APP_CANONICAL },
			{ rel: 'icon', href: process.env.REACT_APP_ICON },
			{ rel: 'manifest', href: process.env.REACT_APP_MANIFEST },
		],
		script: process.env.REACT_APP_SCRIPT,
	};

	return (
		<ScrollToTop>
			<Helmet>
				<title>{headData.title}</title>
				{headData.meta &&
					headData.meta.map((meta, index) =>
						meta.name ? (
							<meta
								key={index}
								name={meta.name}
								content={meta.content}
							/>
						) : (
							<meta
								key={index}
								property={meta.property}
								content={meta.content}
							/>
						),
					)}
				{headData.script && <script type='application/ld+json'>{headData.script}</script>}
				{headData.link &&
					headData.link.map((link, index) => (
						<link
							rel={link.rel}
							href={link.href}
						/>
					))}
			</Helmet>
			<Routes>
				{!publicCms && (
					<Route
						path='/'
						element={
							<Navigate
								to={loginPage}
								replace={true}
							/>
						}
					/>
				)}
				{publicCms && pages.map((pageData: Page) => pageData.toRoute())}
				<Route
					key={'default'}
					path={'*'}
					element={publicCms && pages.length > 0 ? <ErrorPage /> : <></>}
				/>
				{registrationEnabled && (
					<>
						<Route
							path={registrationPage}
							element={pages.length > 0 || isPortalRoute || !publicCms ? <RegisterForm /> : <></>}
						/>
						<Route
							path={foreignRegistrationPage}
							element={pages.length > 0 || isPortalRoute || !publicCms ? <RegisterForeignStudent /> : <></>}
						/>
						<Route
							path={loginPage}
							element={(pages.length > 0 || isPortalRoute || !publicCms) && <LoginForm />}
						/>
						<Route
							path={passwordResetPage}
							element={(pages.length > 0 || isPortalRoute || !publicCms) && <ResetPasswordForm />}
						/>
						<Route
							path={accountPage}
							element={
								<PrivateRoute>
									<AccountPage />
								</PrivateRoute>
							}
						/>
						<Route
							path={panelPage + '/*'}
							element={
								<PrivateRoute>
									<PanelPage />
								</PrivateRoute>
							}
						/>
						<Route
							path={confirmAccountPage}
							element={<ConfirmAccountPage />}
						/>
						<Route
							path='/weryfikacja-email/:token'
							element={<EmailVerification />}
						/>
					</>
				)}
			</Routes>
		</ScrollToTop>
	);
}

const PrivateRoute = (props: { children: React.ReactNode }): JSX.Element => {
	const { children } = props;
	const { isLoggedIn } = useContext(AuthContext);
	const location = useLocation();

	return isLoggedIn() ? (
		<>{children}</>
	) : (
		<Navigate
			replace={true}
			to='/login'
			state={{ from: `${location.pathname}${location.search}` }}
		/>
	);
};

export default App;
